import { makeObservable, override } from 'mobx';
import {
    BorrowerApiFindAllBorrowersRequest,
    BorrowerDto,
    LegalEntityDto,
    PersonDto,
    UpdateLegalEntityRequest,
} from 'src/generated-api-client';
import {
    borrowersApi,
    bpmsResourcesManagerApi,
} from 'src/services/apiServices';
import { ListFilter } from 'src/utils/FormHelper';
import { BasicStore } from 'src/utils/mobx/BasicStore/BasicStore';
import { BasicStoreApi } from 'src/utils/mobx/BasicStore/BasicStore.types';
import { FilterCriteria } from 'src/utils/mobx/FilterCriteria';
import { Pager } from 'src/utils/mobx/Pager';
import { Sorter } from 'src/utils/mobx/Sorter';
import { RequestHelper } from 'src/utils/RequestHelper';

export type BorrowersFilter = ListFilter<BorrowerApiFindAllBorrowersRequest>;

export type BorrowerItemDetails = LegalEntityDto | PersonDto;

export class BorrowersStoreClass extends BasicStore<
    BorrowerItemDetails,
    BorrowersFilter,
    UpdateLegalEntityRequest,
    unknown,
    BorrowerDto
> {
    constructor() {
        super();
        makeObservable(this);
    }

    itemDetailsFormKey = 'embedded:deployment:forms/bo/bo-12-borrower.json';

    itemEditFormKey = 'embedded:deployment:forms/bo/bo-31-edit-borrower.json';

    pager = new Pager();
    sorter = new Sorter();
    filterCriteria: FilterCriteria<BorrowersFilter> = new FilterCriteria({});

    api: BasicStoreApi<
        BorrowerItemDetails,
        UpdateLegalEntityRequest,
        unknown,
        BorrowerDto
    > = {
        loadList: async (...args: any[]) => {
            const options = RequestHelper.getOptionsFromArgs(args);
            const resp = await RequestHelper.unwrapFromAxiosPromise(
                borrowersApi.findAllBorrowers(
                    {
                        ...this.pager,
                        ...this.sorter,
                        ...this.filterCriteria.filter,
                    },
                    options,
                ),
            );

            this.pager.setTotal(resp.totalElements || 0);

            return (resp.content || []) as BorrowerItemDetails[];
        },
        updateItem: (id, data, ...args) => {
            const options = RequestHelper.getOptionsFromArgs(args);

            return RequestHelper.unwrapFromAxiosPromise(
                borrowersApi.updateBorrower(
                    { id: Number(id), updateLegalEntityRequest: data },
                    options,
                ),
            );
        },
        loadItemForm: (formKey: string, ...args) => {
            const options = RequestHelper.getOptionsFromArgs(args);

            return RequestHelper.unwrapFromAxiosPromise(
                bpmsResourcesManagerApi.getForm({ formKey }, options),
            );
        },
        loadItem: (id: number, ...args) => {
            const options = RequestHelper.getOptionsFromArgs(args);

            return RequestHelper.unwrapFromAxiosPromise(
                borrowersApi.getBorrower({ id }, options),
            );
        },
    };

    @override get hasFilter() {
        return Boolean(this.filterCriteria.filter.filterSearch);
    }
}

export const BorrowersStore = new BorrowersStoreClass();
